<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="name"
              v-model="filters.name"
              name="name"
              :label="$t('Nome da unidade de medida')"
              :placeholder="$t('Unidade de medida')"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="units-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('PRODUCT_UNIT.NAME'), 'female', queryExecuted)"
        :fields="fields"
        :items="productUnits"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :is-active="row.item.active"
            :show-update="$can('Update', 'UnitOfMeasurement')"
            :show-delete="$can('Delete', 'UnitOfMeasurement')"
            :show-activate="$can('Activate', 'UnitOfMeasurement')"
            :show-deactivate="$can('Deactivate', 'UnitOfMeasurement')"
            @update="showUpdateUnitSidebar(row)"
            @delete="deleteUnit(row)"
            @activate="activateDeactivateUnit(row)"
            @deactivate="activateDeactivateUnit(row)"
          />
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="units-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <product-unit-sidebar
      ref="unit_sidebar"
      @save="getData"
    />

    <fab
      v-if="$can('Create', 'UnitOfMeasurement')"
      :main-tooltip="$t('Adicionar Unidade')"
      @click="showCreateBrandSidebar"
    />
  </div>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EStatusBadge,
  EPageSizeSelector,
  EPaginationSummary,
  EFilters,
  EGridActions,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { mapActions, mapState } from 'vuex'
import ProductUnitSidebar from './ProductUnitSidebar.vue'

export default {
  name: 'ProductUnit',
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EStatusBadge,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EFilters,
    ProductUnitSidebar,
    EGridActions,
  },

  mixins: [],

  data() {
    return {
      queryExecuted: false,
      showSidebar: false,
      fetching: false,
      edit: false,
      saving: false,
    }
  },

  computed: {
    ...mapState('pages/catalog/productUnits', {
      productUnits: 'productUnits',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Sigla'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          trClass: 'text-center',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '90px' },
          sortable: true,
        },
      ]
    },
  },

  mounted() {},

  methods: {
    ...mapActions('pages/catalog/products', { comboFetchDomainData: 'fetchDomainData' }),
    ...mapActions('pages/catalog/productUnits', [
      'fetchProductUnits',
      'resetFilters',
      'setCurrentPage',
      'setPageSize',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchProductUnits()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    showCreateBrandSidebar() {
      this.$refs.unit_sidebar.show()
    },

    async showUpdateUnitSidebar(row) {
      this.$refs.unit_sidebar.show({ id: row.item.id })
    },

    async deleteUnit(unit) {
      const { id } = unit.item
      try {
        const confirm = await this.confirm()
        if (confirm) {
          await this.$http.delete(`/api/units/${id}`)
          this.comboFetchDomainData('units')
          this.getData()
          this.showSuccess({ message: this.$t('Unidade removida com sucesso') })
        }
      } catch (error) {
        this.showError({ error })
      }
    },

    async activateDeactivateUnit(row) {
      try {
        const confirm = await this.confirm()
        if (confirm) {
          const path = row.item.active ? 'deactivate' : 'activate'
          await this.$http({
            url: `/api/units/${path}/${row.item.id}`,
            method: 'PUT',
          })
          this.comboFetchDomainData('units')
          this.getData()
          this.showSuccess({
            message: this.$t(`${row.item.active === true ? 'Inativado' : 'Ativado'} com sucesso`),
          })
        }
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style></style>
